import { useState, useEffect, useCallback, useRef } from 'react';
import { automationAPI } from '../api/enhancedApiService';
import { debugLog, debugWarn, debugError } from '../utils/debugLogger';

/**
 * Fixed hook for fetching next run times - eliminates infinite loops
 */
export const useNextRunTimes = (connectionId, options = {}) => {
  const {
    refreshInterval = 60000, // 1 minute default
    enabled = true,
    onError = null,
    pauseWhenHidden = true // New option to pause when page is hidden
  } = options;

  const [nextRuns, setNextRuns] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [manualRunLoading, setManualRunLoading] = useState(false);

  // Use refs to avoid dependency issues
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);
  const consecutiveErrorsRef = useRef(0);
  const circuitBreakerRef = useRef(false);
  const currentConnectionIdRef = useRef(connectionId);
  const isPageHiddenRef = useRef(false);

  // Update connection ref when it changes
  useEffect(() => {
    currentConnectionIdRef.current = connectionId;
  }, [connectionId]);

  // Handle page visibility changes to pause polling when page is hidden
  useEffect(() => {
    if (!pauseWhenHidden) return;

    const handleVisibilityChange = () => {
      isPageHiddenRef.current = document.hidden;
      debugLog('[useNextRunTimes] Page visibility changed:', document.hidden ? 'hidden' : 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pauseWhenHidden]);

  // Stable fetch function that doesn't change on every render
  const fetchNextRuns = useCallback(async (isManualRefresh = false) => {
    const currentConnectionId = currentConnectionIdRef.current;

    // Skip if disabled or circuit breaker is open (unless manual refresh)
    if (!enabled || (!isManualRefresh && circuitBreakerRef.current)) {
      return;
    }

    if (!mountedRef.current) {
      debugLog('[useNextRunTimes] Component unmounted, skipping fetch');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;

      if (currentConnectionId) {
        response = await automationAPI.getNextRunTimes(currentConnectionId, {
          forceFresh: true
        });
      } else {
        response = await automationAPI.getAllNextRunTimes({
          forceFresh: true
        });
      }

      // Don't check mountedRef here - React handles setState on unmounted components safely
      // Checking it causes issues with React Strict Mode double-mounting

      let runs = {};

      if (currentConnectionId) {
        runs = response?.next_runs || {};
      } else {
        // When fetching all connections, extract next_runs from each connection's data
        const allSchedules = response?.schedules_by_connection || response?.next_runs_by_connection || {};

        // Transform from { conn_id: { next_runs: {...} } } to { conn_id: {...} }
        runs = {};
        Object.entries(allSchedules).forEach(([connectionId, connectionData]) => {
          if (connectionData?.next_runs) {
            runs[connectionId] = connectionData.next_runs;
          } else {
            // Fallback if data is already in the right format
            runs[connectionId] = connectionData;
          }
        });
      }

      setNextRuns(runs);
      setLastFetchTime(new Date());
      setError(null);
      consecutiveErrorsRef.current = 0;
      circuitBreakerRef.current = false;

    } catch (err) {
      debugError('[useNextRunTimes] Error fetching next run times:', err);

      // Don't check mountedRef - allow state updates even if component unmounted

      consecutiveErrorsRef.current += 1;

      const errorInfo = {
        message: err.message || 'Failed to fetch next run times',
        type: 'api_error',
        consecutiveErrors: consecutiveErrorsRef.current
      };

      setError(errorInfo);

      if (onError) {
        try {
          onError(errorInfo);
        } catch (callbackError) {
          console.error('[useNextRunTimes] Error in onError callback:', callbackError);
        }
      }

      // Open circuit breaker after 3 consecutive errors
      if (consecutiveErrorsRef.current >= 3) {
        console.warn('[useNextRunTimes] Opening circuit breaker due to consecutive errors');
        circuitBreakerRef.current = true;

        // Auto-reset circuit breaker after 5 minutes
        setTimeout(() => {
          if (mountedRef.current) {
            console.log('[useNextRunTimes] Auto-resetting circuit breaker');
            circuitBreakerRef.current = false;
            consecutiveErrorsRef.current = 0;
          }
        }, 5 * 60 * 1000);
      }
    } finally {
      // Always set loading to false - React handles setState on unmounted components safely
      setLoading(false);
    }
  }, [enabled, onError]); // Minimal dependencies

  // Manual refresh function
  const refresh = useCallback(async () => {
    console.log('[useNextRunTimes] Manual refresh requested');
    circuitBreakerRef.current = false;
    consecutiveErrorsRef.current = 0;
    await fetchNextRuns(true);
  }, [fetchNextRuns]);

  // Manual trigger function
  const triggerManualRun = useCallback(async (automationType) => {
    const currentConnectionId = currentConnectionIdRef.current;

    if (!currentConnectionId) {
      console.warn('[useNextRunTimes] Cannot trigger manual run without connectionId');
      return false;
    }

    try {
      setManualRunLoading(true);
      console.log(`[useNextRunTimes] Triggering manual run for ${automationType} on connection ${currentConnectionId}`);
      const response = await automationAPI.triggerImmediate(currentConnectionId, automationType);

      // Clear the button-loading state IMMEDIATELY once the API call returns.
      // The previous implementation deferred this 2 seconds AND gated it on
      // mountedRef.current, so any HMR / parent re-render / StrictMode
      // remount between trigger and the setTimeout firing left the button
      // stuck on "Running..." forever. The 2-second delay was only there to
      // give the backend time to update job state before refetching — that
      // concern is now separated from the button-state concern (see below).
      setManualRunLoading(false);

      // Refresh next-run times after a short delay. Still gated on
      // mountedRef to avoid an unnecessary fetch on unmount, but the
      // button state above is independent of this.
      setTimeout(() => {
        if (mountedRef.current) {
          fetchNextRuns();
        }
      }, 2000);

      return response?.success || response?.result || !response?.error;
    } catch (error) {
      console.error('[useNextRunTimes] Error triggering manual run:', error);
      setManualRunLoading(false);
      return false;
    }
  }, [fetchNextRuns]);

  // Single effect to handle polling - OPTIMIZED to prevent mounting loops
  useEffect(() => {
    debugLog('[useNextRunTimes] Setting up polling effect', {
      enabled,
      connectionId,
      refreshInterval
    });

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only start if enabled (connectionId can be null to fetch all connections)
    if (!enabled) {
      debugLog('[useNextRunTimes] Polling disabled');
      return;
    }

    // Fetch immediately on mount - no debounce needed with proper dependency array
    if (mountedRef.current && !circuitBreakerRef.current) {
      fetchNextRuns();
    }

    // Set up recurring interval with better conditions
    intervalRef.current = setInterval(() => {
      if (mountedRef.current && !circuitBreakerRef.current && enabled) {
        // Skip if page is hidden and pauseWhenHidden is enabled
        if (pauseWhenHidden && isPageHiddenRef.current) {
          debugLog('[useNextRunTimes] Skipping fetch - page is hidden');
          return;
        }
        fetchNextRuns();
      }
    }, refreshInterval);

    debugLog('[useNextRunTimes] Polling started with interval:', refreshInterval);

    // Cleanup function
    return () => {
      debugLog('[useNextRunTimes] Cleaning up polling');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, connectionId, refreshInterval]); // Stable dependencies only

  // Reset errors when connectionId changes
  useEffect(() => {
    setError(null);
    circuitBreakerRef.current = false;
    consecutiveErrorsRef.current = 0;
  }, [connectionId]);

  // Cleanup on unmount - OPTIMIZED
  useEffect(() => {
    return () => {
      debugLog('[useNextRunTimes] Component unmounting');
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  return {
    nextRuns,
    loading,
    error,
    lastFetchTime,
    refresh,
    triggerManualRun,
    manualRunLoading,
    circuitBreakerOpen: circuitBreakerRef.current,
    consecutiveErrors: consecutiveErrorsRef.current
  };
};